import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const AddProject = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ⭐ EDIT MODE (Data coming from AdminDashboard)
  const editData = location.state?.editData || null;

  // ⭐ State
  const [project, setProject] = useState({
    projectName: "",
    projectLocation: "",
    clientId: "",
    devices: [{ name: "", deviceId: "", info: "" }],
  });

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ⭐ PREFILL FORM WHEN IN EDIT MODE
  useEffect(() => {
    if (editData) {
      setProject({
        projectName: editData.projectName || "",
        projectLocation: editData.projectLocation || "",
        clientId: editData.clientId || "",
        devices: editData.devices?.length
          ? editData.devices
          : [{ name: "", deviceId: "", info: "" }],
      });
    }
  }, [editData]);

  // Fetch all users for dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          "https://api.livestockify.com/api/chennai/users",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (Array.isArray(response.data)) setUsers(response.data);
        else if (Array.isArray(response.data.data))
          setUsers(response.data.data);
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    };

    fetchUsers();
  }, []);

  // Handle text fields
  const handleChange = (e) => {
    setProject({ ...project, [e.target.name]: e.target.value });
  };

  // Handle devices change
  const handleDeviceChange = (index, field, value) => {
    const updatedDevices = [...project.devices];
    updatedDevices[index][field] = value;
    setProject({ ...project, devices: updatedDevices });
  };

  const handleAddDevice = () => {
    setProject({
      ...project,
      devices: [...project.devices, { name: "", deviceId: "", info: "" }],
    });
  };

  const handleDeleteDevice = (index) => {
    const updatedDevices = project.devices.filter((_, i) => i !== index);
    setProject({ ...project, devices: updatedDevices });
  };

  // ⭐ FINAL SUBMIT (Add or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("token");

      const payload = {
        projectName: project.projectName,
        projectLocation: project.projectLocation,
        clientId: project.clientId,
        devices: project.devices,
      };

      let response;

      if (editData) {
        // ⭐ UPDATE MODE
        const updateUrl = `https://api.livestockify.com/api/chennai/projects/${editData.projectUuid}`;
        response = await axios.put(updateUrl, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        setSuccess("Project updated successfully!");
      } else {
        // ⭐ CREATE MODE
        response = await axios.post(
          "https://api.livestockify.com/api/chennai/projects",
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setSuccess("Project created successfully!");
      }

      setTimeout(() => navigate("/chennai/admin/dashboard"), 1200);
    } catch (err) {
      console.error("❌ Error:", err);
      setError("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full bg-white flex flex-col items-center min-h-screen py-10 px-6 md:px-20">
      {/* Back Button */}
      <button
        onClick={() => navigate("/chennai/admin/dashboard")}
        className="text-gray-700 mb-6 self-start flex items-center font-semibold gap-2 hover:underline"
      >
        ← Back to dashboard
      </button>

      {/* Main Container */}
      <div className="bg-white  p-8 w-full max-w-2xl">
        <h2 className="text-[#34C759] text-xl font-semibold sm:w-48 text-left pr-4 mb-5 ml-[-75px]">
          {editData ? "Update Project" : "Create New Project"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Project Name */}
          <div className="flex flex-col sm:flex-row sm:justify-between">
            <label className="text-gray-700 font-medium sm:w-40 text-right  pr-6">
              Project Name
            </label>
            <input
              type="text"
              name="projectName"
              value={project.projectName}
              onChange={handleChange}
              required
              placeholder="Enter Project Name"
              className="border border-gray-300 rounded-md px-3 py-2 sm:w-2/3 focus:ring-[#34C759]"
            />
          </div>

          {/* Project Location */}
          <div className="flex flex-col sm:flex-row sm:justify-between">
            <label className="text-gray-700 font-medium sm:w-40 text-right pr-6" >
              Project Location
            </label>
            <input
              type="text"
              name="projectLocation"
              value={project.projectLocation}
              onChange={handleChange}
              required
              placeholder="Enter Project Location"
              className="border border-gray-300 rounded-md px-3 py-2 sm:w-2/3 focus:ring-[#34C759]"
            />
          </div>

          {/* User Dropdown */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[#34C759] text-lg font-semibold sm:w-48 text-left pr-4">
                Select User
              </h3>

              <button
                onClick={() => navigate("/chennai/admin/add-user")}
                type="button"
                className="text-[#4498D8] hover:underline whitespace-nowrap "
              >
                Create new user
              </button>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between">
              <label className="text-gray-700 font-medium sm:w-40 text-right pr-6">User</label>

              <select
                name="clientId"
                value={project.clientId}
                onChange={handleChange}
                required
                className="border border-gray-300 rounded-md px-3 py-2 sm:w-2/3 focus:ring-[#34C759]"
              >
                <option value="">Select User</option>
                {users.map((u) => (
                  <option key={u.userUuid} value={u.userUuid}>
                    {u.userFirstname} {u.userLastname}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Device Section */}
          <div>
            <h3 className="text-[#34C759] text-lg font-semibold  sm:w-48 text-left pr-6 mb-6 mt-20 ml-[-55px]">
              Add New Device
            </h3>

            {project.devices.map((device, index) => (
              <div key={index} className="space-y-3 mb-6 border-b pb-4">
                {/* Device ID */}
                <div className="flex flex-col sm:flex-row sm:justify-between">
                  <label className="text-gray-700 font-medium sm:w-40 text-right pr-6">
                    Device ID
                  </label>
                  <input
                    type="text"
                    value={device.deviceId}
                    onChange={(e) =>
                      handleDeviceChange(index, "deviceId", e.target.value)
                    }
                    placeholder="Enter Device ID"
                    required
                    className="border border-gray-300 rounded-md px-3 py-2 sm:w-2/3 focus:ring-[#34C759]"
                  />
                </div>

                {/* Device Name */}
                <div className="flex flex-col sm:flex-row sm:justify-between">
                  <label className="text-gray-700 font-medium sm:w-40 text-right  pr-6">
                    Device Name
                  </label>
                  <input
                    type="text"
                    value={device.name}
                    onChange={(e) =>
                      handleDeviceChange(index, "name", e.target.value)
                    }
                    placeholder="Enter Device Name"
                    required
                    className="border border-gray-300 rounded-md px-3 py-2 sm:w-2/3 focus:ring-[#34C759]"
                  />
                </div>

                {/* Device Info */}
                <div className="flex flex-col sm:flex-row sm:justify-between">
                  <label className="text-gray-700 font-medium sm:w-40 text-right  pr-6">
                    Device Info
                  </label>
                  <input
                    type="text"
                    value={device.info}
                    onChange={(e) =>
                      handleDeviceChange(index, "info", e.target.value)
                    }
                    placeholder="Enter Device Info"
                    required
                    className="border border-gray-300 rounded-md px-3 py-2 sm:w-2/3 focus:ring-[#34C759]"
                  />
                </div>

                {/* Delete Device Button */}
                {project.devices.length > 1 && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleDeleteDevice(index)}
                      className="bg-white text-[#FFA200] px-4 py-1 rounded-md flex items-center gap-2  border border-[#FFA200]"
                    >
                      Delete
                      <img src="http://res.cloudinary.com/dt8emxboh/image/upload/v1763401884/wn7veuichtzpm3vy6ikk.png" alt="delete" />
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Add More Device */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleAddDevice}
                className="bg-[#4498D8] text-white px-6 py-2 rounded-md hover:bg-[#3182CE]"
              >
                Add One More
              </button>
            </div>
          </div>

          {/* Status */}
          {error && <p className="text-[#FFA200] text-center">{error}</p>}
          {success && <p className="text-[#34C759] text-center">{success}</p>}

          {/* Submit */}
          <div className="flex justify-center mt-6">
            <button
              type="submit"
              disabled={loading}
              className="bg-[#34C759] text-white px-8 py-2 md:px-20 lg:px-40 rounded-md hover:bg-[#2EB24F]"
            >
              {loading ? "Saving..." : editData ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProject;
